import { useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { errorMessage } from '../hooks/useApi';

/** Shown before anything else while an account is still on the temporary password it was given. */
export function ChangePasswordPage() {
  const { changePassword, logout, user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== again) {
      setError('The two new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full px-3 py-2 bg-[#171022] border border-[#2c2140] rounded-lg text-sm text-white focus:outline-none focus:border-violet-500';

  return (
    <div className="min-h-full flex items-center justify-center bg-[#090814] p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-[#120d1a] border border-[#241b33] rounded-2xl p-7 shadow-2xl space-y-4"
      >
        <div>
          <h1 className="text-xl font-bold text-white">Choose your own password</h1>
          <p className="text-xs text-gray-400 mt-1">
            {user?.email} is using a temporary password. Pick a new one to continue: at least 10
            characters, with letters and numbers.
          </p>
        </div>

        <label className="block">
          <span className="block text-xs font-semibold text-gray-300 mb-1">Temporary password</span>
          <input type="password" required autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-gray-300 mb-1">New password</span>
          <input type="password" required autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={field} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-gray-300 mb-1">New password again</span>
          <input type="password" required autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} className={field} />
        </label>

        {error && (
          <p role="alert" className="text-xs text-rose-300">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-60"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Save new password
        </button>
        <button
          type="button"
          onClick={() => void logout()}
          className="w-full text-xs text-gray-400 hover:text-white"
        >
          Sign out instead
        </button>
      </form>
    </div>
  );
}
